import { AsyncAction, portalAction, PortalThunk, postTag, updateTag, tagsSelector, setIsBiometricEnabledOnServer, setBiometricLoginStatus } from '../'
import { get, parseCreationOptionsFromJSON, parseRequestOptionsFromJSON } from '@github/webauthn-json/browser-ponyfill'
import { formatISO } from 'date-fns'
import {
  BiometricFinishAuthenticationRequest,
  BiometricLoginStatus,
  BiometricStartAuthenticationResponse,
  BIOMETRIC_DENIED_COUNT_KEY,
  BIOMETRIC_LAST_DENIED_DATE_KEY,
  GetConsumerCredentialsResponse,
  StartRegistrationResponseData,
} from '../../model'
import { getBiometricSettings, getCredentialIds, setBiometricSettings } from '@afterpay/utils/biometric'
import { EMPTY_ALLOW_CREDENTIALS_ERROR, NULL_ALLOW_CREDENTIALS_ERROR } from '../../component/LoginPassword/constants'
import { getUsername } from '../../component/LoginPassword/utils'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { startBiometricRegistration, finishBiometricRegistration } from './api'

export const getConsumerCredentials: PortalThunk<void, GetConsumerCredentialsResponse> = portalAction('consumers/fido2/credentials/get', async (_, thunk) => {
  return await thunk.extra.portalApi().get('/portal/consumers/fido2/credentials')
})

export const cleanUpConsumerCredentials =
  (email: string | undefined, mobile: string | undefined): AsyncAction<void> =>
  async (dispatch) => {
    const [storedCredentialIdsByEmail, storedCredentialIdsByMobile] = getCredentialIds(email, mobile)

    const storedCredentials = Array.from(new Set([...storedCredentialIdsByEmail, ...storedCredentialIdsByMobile]))

    if (storedCredentials.length === 0) {
      return
    }

    const remoteCredentials = await dispatch(getConsumerCredentials()).unwrap()
    const remoteCredentialIds = remoteCredentials.map((credential) => credential.id)

    const validCredentialIds = storedCredentials.filter((credential) => remoteCredentialIds.includes(credential))

    const biometricSettings = getBiometricSettings()

    if (valueIsNotNullOrUndefined(email)) {
      biometricSettings.credentials[email.toLowerCase()] = validCredentialIds
    }
    if (valueIsNotNullOrUndefined(mobile)) {
      biometricSettings.credentials[mobile] = validCredentialIds
    }

    setBiometricSettings(biometricSettings)
  }

export const setLocalBiometricConsumerCredentials = (
  email: string | undefined,
  mobile: string | undefined,
  storedCredentialIdsByEmail: string[],
  storedCredentialIdsByMobile: string[],
  credentialIds: string[]
): void => {
  const biometricSettings = getBiometricSettings()

  storedCredentialIdsByEmail.push(...credentialIds)
  storedCredentialIdsByMobile.push(...credentialIds)

  if (email !== null && email !== undefined) {
    biometricSettings.credentials[email.toLowerCase()] = Array.from(new Set([...storedCredentialIdsByEmail]))
  }

  if (mobile !== null && mobile !== undefined) {
    biometricSettings.credentials[mobile] = Array.from(new Set([...storedCredentialIdsByMobile]))
  }

  setBiometricSettings(biometricSettings)
}

export const getStartRegistrationResponseData = (): AsyncAction<StartRegistrationResponseData> => async (dispatch) => {
  // start
  const startBiometricRegistrationData = await dispatch(startBiometricRegistration()).unwrap()
  const creationOptions = parseCreationOptionsFromJSON({ publicKey: startBiometricRegistrationData.publicKeyCredentialCreationOptions })

  return {
    startBiometricRegistrationData,
    creationOptions,
  }
}

export const getFinishBiometricRegistrationCredentialIds =
  (requestId: string, credential: any): AsyncAction<string[]> =>
  async (dispatch) => {
    // finish
    await dispatch(finishBiometricRegistration({ requestId, credential })).unwrap()

    return [credential.id]
  }

export const dismissBiometricRegistrationModal = (): AsyncAction<number> => async (dispatch, getState) => {
  const { consumerTag } = tagsSelector(getState())

  if (BIOMETRIC_DENIED_COUNT_KEY in consumerTag && BIOMETRIC_LAST_DENIED_DATE_KEY in consumerTag) {
    const currCount = parseInt(consumerTag[BIOMETRIC_DENIED_COUNT_KEY])

    const deniedCount = currCount + 1
    dispatch(updateTag({ name: BIOMETRIC_DENIED_COUNT_KEY, data: `${deniedCount}` }))
    dispatch(updateTag({ name: BIOMETRIC_LAST_DENIED_DATE_KEY, data: formatISO(new Date(), { representation: 'date' }) }))

    return deniedCount
  }

  dispatch(postTag({ name: BIOMETRIC_DENIED_COUNT_KEY, data: '1' }))
  dispatch(postTag({ name: BIOMETRIC_LAST_DENIED_DATE_KEY, data: formatISO(new Date(), { representation: 'date' }) }))

  return 1
}

const BASE_URL = '/portal/consumers/fido2/authenticate'

export const startBiometricAuthentication: PortalThunk<string, BiometricStartAuthenticationResponse> = portalAction(
  'biometric/startAuthentication',
  async (username, thunk) => {
    const payload = {
      username,
    }
    return await thunk.extra.portalApi().post(BASE_URL, payload)
  }
)

export const finishBiometricAuthentication: PortalThunk<BiometricFinishAuthenticationRequest, any> = portalAction(
  'biometric/finishAuthentication',
  async (request, thunk) => {
    return await thunk.extra.portalApi().post(`${BASE_URL}/finish`, request)
  }
)

export const checkIsBiometricEnabledOnServer =
  (email: string | undefined, mobile: string | undefined, isMobileLogin: boolean): AsyncAction<boolean> =>
  async (dispatch) => {
    const username = getUsername(isMobileLogin, mobile, email)

    // add cache check
    try {
      const startResult = await dispatch(startBiometricAuthentication(username)).unwrap()
      const allowCredentials = startResult.publicKeyCredentialRequestOptions?.allowCredentials

      const isEnabled = Array.isArray(allowCredentials) && allowCredentials.length > 0
      dispatch(setIsBiometricEnabledOnServer(isEnabled))

      return isEnabled
    } catch (e) {
      console.error('Error authenticating biometrics on the server', e)
      return false
    }
  }

export const biometricLogin =
  (email: string | undefined, mobile: string | undefined, isMobileLogin: boolean): AsyncAction<void> =>
  async (dispatch) => {
    dispatch(setBiometricLoginStatus(BiometricLoginStatus.Started))

    const username = getUsername(isMobileLogin, mobile, email)

    // start
    const startResult = await dispatch(startBiometricAuthentication(username)).unwrap()
    const allowCredentials = startResult.publicKeyCredentialRequestOptions?.allowCredentials
    if (Array.isArray(allowCredentials) && allowCredentials.length === 0) {
      throw new Error(EMPTY_ALLOW_CREDENTIALS_ERROR)
    }

    if (allowCredentials === null) {
      throw new Error(NULL_ALLOW_CREDENTIALS_ERROR)
    }

    const [storedCredentialIdsByEmail, storedCredentialIdsByMobile] = getCredentialIds(email, mobile)

    const storedCredentialIds = Array.from(new Set([...storedCredentialIdsByEmail, ...storedCredentialIdsByMobile]))

    const allowedCredentialIds = allowCredentials?.map((credential) => credential.id) ?? []

    // check if the credentials in local storage is returned in the server
    const validCredentialInSettings = allowedCredentialIds.some((credentialId) => storedCredentialIds.includes(credentialId))

    const options = parseRequestOptionsFromJSON({ publicKey: startResult.publicKeyCredentialRequestOptions })

    // get
    const credential = await get(options)

    // finish
    await dispatch(
      finishBiometricAuthentication({
        requestId: startResult.requestId,
        credential: credential as PublicKeyCredential,
      })
    ).unwrap()

    // set local storage if no storage currently but successfully authenticated with biometric
    if (!validCredentialInSettings) {
      setLocalBiometricConsumerCredentials(email, mobile, storedCredentialIdsByEmail, storedCredentialIdsByMobile, [credential.id])
    }
  }
